import { useEffect, useState, useCallback, useRef } from "react";
import {
  groupMessagesIntoConversations,
  ConversationSummary,
} from "../lib/chatUtils";
import toast from "react-hot-toast";
import { supabase } from "../supabaseClient";
import {
  getMessageHistoryOnce,
  invalidateHistory
} from "./messageHistoryClient";

export function useConversationHistory({
  activePhone,
  isHistoryPage,
  myUsers,
  session,
  setActivePhone,
  setSelectedConversationId,
  setSelectedConversationLastDate,
  dateFilter,
}: any) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [userConversations, setUserConversations] = useState<
    ConversationSummary[]
  >([]);
  const [conversationCounts, setConversationCounts] = useState<{
    [phone: string]: number;
  }>({});
  const [usersWithHistory, setUsersWithHistory] = useState<Set<string>>(
    new Set()
  );
  const [loadingUsersWithHistory, setLoadingUsersWithHistory] = useState(true);
  const [selectedUserData, setSelectedUserData] = useState<any>(null);
  const [dataSent, setDataSent] = useState<string | null>(null);
  const [error, setError] = useState<{ message: string; code: string } | null>(
    null
  );

  const fetchMessageHistory = useCallback(
    async (phoneNumber: string, customDateFilter?: { mode: "single" | "range"; from?: string; to?: string }, userId?: string | number) => {
      try {
        if (loadingUsersWithHistory) return;
        
        const filterToUse = isHistoryPage ? (customDateFilter || dateFilter) : undefined;
        
        let fetchParams: any = {
          phone: phoneNumber,
          session,
          ttlMs: 1000,
        };

        if (userId) {
          fetchParams.userId = userId;
        } else {
          const user = myUsers.find((u: any) => u.phone === phoneNumber);
          if (user?.id) {
            fetchParams.userId = user.id;
          } 
        }

        if (isHistoryPage && filterToUse && (filterToUse.from || filterToUse.to)) {
          if (filterToUse.mode === "single" && filterToUse.from) {
            fetchParams.from = filterToUse.from;
            fetchParams.to = filterToUse.from;
          } else if (filterToUse.mode === "range") {
            if (filterToUse.from) fetchParams.from = filterToUse.from;
            if (filterToUse.to) fetchParams.to = filterToUse.to;
          }
        }

        const data = await getMessageHistoryOnce(fetchParams);

        if (!data || !Array.isArray(data)) {
          console.warn("No valid data received for", phoneNumber);
          setUserConversations([]);
          setConversationCounts((prev: any) => ({
            ...prev,
            [phoneNumber]: 0,
          }));
          return;
        }

        const conversations = groupMessagesIntoConversations(data);
        
        setUserConversations(conversations);

        if (!isHistoryPage) {
          const limitedMessages = data.slice(-100).map((msg: any) => {
            let messageType: "text" | "image" | "audio" | "assistant" | "document" = "text";

            if (msg.mediaType === "audio" || msg.media_type === "audio") {
              messageType = "audio";
            } else if (msg.mediaType === "image" || msg.media_type === "image") {
              messageType = "image";
            } else if (msg.mediaType === "document" || msg.media_type === "document") {
              messageType = "document";
            } else if (msg.messageType === "assistant") {
              messageType = "assistant";
            }

            return {
              from: msg.from,
              body: msg.body,
              dateSent: msg.dateSent,
              sid: msg.message_sid || msg.sid,
              isReply: msg.isReply || msg.is_reply || false,
              originalMessageSid: msg.originalMessageSid || msg.original_message_sid,
              replyToBody: msg.replyToBody || msg.reply_to_body,
              replyToMediaUrl: msg.replyToMediaUrl || msg.reply_to_media_url || msg.replyto_media_url,
              replyToMediaType: msg.replyToMediaType || msg.reply_to_media_type || msg.replyto_media_type,
              replyToFileName: msg.replyToFileName || msg.reply_to_file_name || msg.replyto_file_name,
              replyToFileSize: msg.replyToFileSize || msg.reply_to_file_size || msg.replyto_file_size,
              type: msg.from === "user" 
                    ? "received" as "received"
                    : "sent" as "sent",
              sender: msg.from,
              message: msg.body,
              date_time: msg.dateSent,
              direct_response: msg.direct_response || false,
              context: msg.context,
              user_id: msg.user_id,
              assistant_id: msg.assistant_id,
              media_url: msg.mediaUrl || msg.media_url,
              media_type: msg.mediaType || msg.media_type,
              file_name: msg.fileName || msg.file_name,
              file_size: msg.fileSize || msg.file_size,
              imageUrl: msg.mediaUrl || msg.media_url,
              fileName: msg.fileName || msg.file_name,
              fileSize: msg.fileSize || msg.file_size,
              messageType: messageType,
            };
          });
          setMessages(limitedMessages);
        } else {
          setMessages([]);
        }

        setConversationCounts((prev: any) => ({
          ...prev,
          [phoneNumber]: conversations.length,
        }));

        if (data.length > 0) {
          setDataSent(data[data.length - 1]?.dateSent);
        }

        const user = myUsers.find((u: any) => u.phone === phoneNumber);
        if (user) {
          setUsersWithHistory((prev) => {
            const updated = new Set(prev);
            if (conversations.length > 0) updated.add(String(user.id));
            else updated.delete(String(user.id));
            return updated;
          });
        }
      } catch (err: any) {
        console.error("Error fetching message history:", err);
        setError({ message: err.message, code: "FETCH_HISTORY_ERROR" });
      }
    },
    [session, myUsers, isHistoryPage, dateFilter, loadingUsersWithHistory]
  );

  const fetchAllHistories = useCallback(async (customDateFilter?: { mode: "single" | "range"; from?: string; to?: string }) => {
    setLoadingUsersWithHistory(false);
  }, []);

  const handleSendMessage = async (
    messageText: string,
    phoneNumber: string | null,
    selectedUser: any,
    aiBlock: boolean,
    imageFile?: File,
    audioFile?: File,
    documentFile?: File
  ) => {
    if (!phoneNumber || !selectedUser || !session) {
      console.error("Missing required parameters:", { phoneNumber, selectedUser: !!selectedUser, session: !!session });
      return;
    }

    if (!selectedUser.id || !session.user?.id) {
      console.error("Missing user IDs:", { userId: selectedUser.id, assistantId: session.user?.id });
      setError({
        message: "Missing user information. Please try again.",
        code: "MSG-002"
      });
      return;
    }

    if (documentFile) {
      try {
        const tempId = `temp_document_${Date.now()}_${Math.random()}`;
        
        const documentMessageData: any = {
          from: "assistant",
          body: messageText || `📄 ${documentFile.name}`,
          type: "sent" as const,
          dateSent: new Date().toISOString(),
          messageType: "document",
          sender: "assistant",
          message: messageText || `📄 ${documentFile.name}`,
          date_time: new Date().toISOString(),
          direct_response: aiBlock ?? false,
          context: null,
          user_id: selectedUser.id,
          assistant_id: session.user.id,
          media_url: null,
          media_type: documentFile.type || 'application/pdf',
          file_name: documentFile.name,
          file_size: documentFile.size,
          imageUrl: null,
          fileName: documentFile.name,
          fileSize: documentFile.size,
          tempId: tempId,
        };

        setMessages(prev => [...prev, documentMessageData]);

        try {

          const formData = new FormData();
          formData.append('document', documentFile); 
          formData.append('phoneNumber', phoneNumber);
          
          if (messageText && messageText.trim()) {
            formData.append('message', messageText);
          }

          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/twilio/whatsapp/sendDocument`,
            {
              method: "POST",
              headers: {
                Authorization: `${session.token_type} ${session.access_token}`,
              },
              body: formData,
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            console.error("WhatsApp document send failed:", {
              status: response.status,
              statusText: response.statusText,
              error: errorText
            });
            
            setMessages(prev => prev.filter(msg => msg.tempId !== tempId));
            
            setError({
              message: `Failed to send document: ${errorText}`,
              code: "DOC-002"
            });
            return;
          }

          const result = await response.json();

          if (result.mediaUrl) {
            setMessages(prev => prev.map(msg => 
              msg.tempId === tempId 
                ? { ...msg, media_url: result.mediaUrl, imageUrl: result.mediaUrl }
                : msg
            ));
          }

        } catch (whatsappError) {
          console.error("WhatsApp document send error:", whatsappError);
          
          setMessages(prev => prev.filter(msg => msg.tempId !== tempId));
          
          setError({
            message: "Failed to send document. Please try again.",
            code: "DOC-003"
          });
          return;
        }

      } catch (error) {
        console.error("Error preparing document:", error);
        setError({
          message: "Failed to prepare document. Please try again.",
          code: "DOC-001"
        });
        return;
      }
    } else if (audioFile) {
      try {
        const fileExtension = audioFile.name.split('.').pop() || 'ogg';
        const fileName = `${phoneNumber}_${Date.now()}.${fileExtension}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('chat-audio')
          .upload(fileName, audioFile, {
            contentType: audioFile.type,
            upsert: false
          });

        if (uploadError) {
          console.error("Supabase upload error:", uploadError);
          throw new Error(`Upload failed: ${uploadError.message}`);
        }

        const { data: urlData } = supabase.storage
          .from('chat-audio')
          .getPublicUrl(fileName);

        if (!urlData?.publicUrl) {
          throw new Error('Failed to get public URL from Supabase');
        }

        const tempId = `temp_audio_${Date.now()}_${Math.random()}`;
        const audioMessageData: any = {
          from: "assistant",
          body: messageText || `🎵 ${audioFile.name}`,
          type: "sent" as const,
          dateSent: new Date().toISOString(),
          messageType: "audio",
          sender: "assistant",
          message: messageText || `🎵 ${audioFile.name}`,
          date_time: new Date().toISOString(),
          direct_response: aiBlock ?? false,
          context: null,
          user_id: selectedUser.id,
          assistant_id: session.user.id,
          media_url: urlData.publicUrl,
          media_type: audioFile.type || 'audio/ogg',
          file_name: audioFile.name,
          file_size: audioFile.size,
          imageUrl: urlData.publicUrl,
          fileName: audioFile.name,
          fileSize: audioFile.size,
          tempId: tempId, 
        };

        setMessages(prev => [...prev, audioMessageData]);

        try {
          const formData = new FormData();
          formData.append('audio', audioFile); 
          formData.append('message', messageText || `🎵 ${audioFile.name}`);
          formData.append('phoneNumber', phoneNumber);

          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/twilio/whatsapp/sendAudio`,
            {
              method: "POST",
              headers: {
                Authorization: `${session.token_type} ${session.access_token}`,
              },
              body: formData,
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            console.error("WhatsApp send failed:", response.status, errorText);
            setMessages(prev => prev.filter(msg => msg.tempId !== tempId));
          } 
        } catch (whatsappError) {
          console.error("WhatsApp send error:", whatsappError);
          setMessages(prev => prev.filter(msg => msg.tempId !== tempId));
        }

      } catch (error) {
        console.error("Error uploading audio:", error);
        setError({
          message: "Failed to send audio. Please try again.",
          code: "AUDIO-001"
        });
        return;
      }
    } else if (imageFile) {
      try {
        const fileExtension = imageFile.name.split('.').pop() || 'jpg';
        const fileName = `${phoneNumber}_${Date.now()}.${fileExtension}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('chat-images')
          .upload(fileName, imageFile, {
            contentType: imageFile.type,
            upsert: false
          });

        if (uploadError) {
          console.error("Supabase upload error:", uploadError);
          throw new Error(`Upload failed: ${uploadError.message}`);
        }

        const { data: urlData } = supabase.storage
          .from('chat-images')
          .getPublicUrl(fileName);

        if (!urlData?.publicUrl) {
          throw new Error('Failed to get public URL from Supabase');
        }

        const realImageUrl = urlData.publicUrl;

        const tempId = `temp_image_${Date.now()}_${Math.random()}`;
        const imageMessageData: any = {
          from: "assistant",
          body: messageText || `📷 ${imageFile.name}`,
          type: "sent" as const,
          dateSent: new Date().toISOString(),
          messageType: "image",
          sender: "assistant",
          message: messageText || `📷 ${imageFile.name}`,
          date_time: new Date().toISOString(),
          direct_response: aiBlock ?? false,
          context: null,
          user_id: selectedUser.id,
          assistant_id: session.user.id,
          media_url: realImageUrl,
          media_type: imageFile.type || 'image/jpeg',
          file_name: imageFile.name,
          file_size: imageFile.size,
          imageUrl: realImageUrl,
          fileName: imageFile.name,
          fileSize: imageFile.size,
          tempId: tempId, 
        };

        setMessages(prev => [...prev, imageMessageData]);

        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/twilio/whatsapp/sendMedia`,
            {
              method: "POST",
              headers: {
                Authorization: `${session.token_type} ${session.access_token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                message: messageText || `📷 ${imageFile.name}`,
                phoneNumber: phoneNumber,
                mediaUrl: realImageUrl,
              }),
            }
          );

          if (!response.ok) {
            console.error("WhatsApp send failed:", response.status, response.statusText);
            setMessages(prev => prev.filter(msg => msg.tempId !== tempId));
          } 
        } catch (whatsappError) {
          console.error("WhatsApp send error:", whatsappError);
          setMessages(prev => prev.filter(msg => msg.tempId !== tempId));
        }

      } catch (error) {
        console.error("Error uploading image:", error);
        setError({
          message: "Failed to send image. Please try again.",
          code: "IMG-001"
        });
        return;
      }
    } else {
      // Handle text messages
      const tempId = `temp_text_${Date.now()}_${Math.random()}`;
      const messageData: any = {
        from: "assistant",
        body: messageText,
        type: "sent" as const,
        dateSent: new Date().toISOString(),
        messageType: "text",
        sender: "assistant",
        message: messageText,
        date_time: new Date().toISOString(),
        direct_response: aiBlock ?? false,
        context: null,
        user_id: selectedUser.id,
        assistant_id: session.user.id,
        tempId: tempId,
      };

      setMessages(prev => [...prev, messageData]);

      try {
        const requestBody = {
          message: messageText,
          phoneNumber: phoneNumber,
          userId: selectedUser.id,
          assistantId: session.user.id,
          directResponse: aiBlock ?? false,
        };

        const response_save = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/twilio/whatsapp/sendAndSave`,
          {
            method: "POST",
            headers: {
              Authorization: `${session.token_type} ${session.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response_save.ok) {
          const errorText = await response_save.text();
          console.error("WhatsApp send and save failed:", {
            status: response_save.status,
            statusText: response_save.statusText,
            error: errorText
          });
          throw new Error(`Failed to send message: ${errorText}`);
        }

      } catch (error) {
        console.error("Error sending message:", error);
        setError({
          message: "Failed to send message. Please try again.",
          code: "MSG-001"
        });
        
        setMessages(prev => prev.filter(msg => msg.tempId !== tempId));
        return;
      }
    }

    invalidateHistory(phoneNumber);
  };

  useEffect(() => {
    if (!session || !activePhone || !selectedUserData?.id) return;

    const userId = selectedUserData.id;
    const channel = supabase
      .channel(`realtime-comm-log-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "communication_log",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row: any = payload.new;

          if (row.sender === "assistant" && row.assistant_id === session.user.id) {
            return;
          }

          const isImageMessage = row.message?.includes('[Imagen recibida:') || row.message?.includes('[Image received:');
          const isAudioMessage = row.message?.includes('[Audio recibido:') || row.message?.includes('[Audio received:');
          const isDocumentMessage = row.message?.includes('[Documento recibido:') || row.message?.includes('[Document received:');
          
          if (isDocumentMessage) {
            if (!row.media_url || !row.media_type || !row.file_name) {
              return;
            }

            const filenameMatch = row.message?.match(/\[Documento recibido: (.+?)\]/) || row.message?.match(/\[Document received: (.+?)\]/);
            const filename = filenameMatch ? filenameMatch[1] : null;
            
            if (filename) {
              const currentTime = new Date(row.date_time).getTime();
              const timeWindow = 5000;
              
              setMessages((prev: any) => {
                const isDuplicate = prev.some((m: any) => {
                  if (m.media_url === row.media_url || m.imageUrl === row.media_url) {
                    return true;
                  }
                  
                  const msgTime = new Date(m.date_time || m.dateSent).getTime();
                  const timeDiff = Math.abs(currentTime - msgTime);
                  
                  if (timeDiff < timeWindow && 
                     ((m.fileName === filename || m.file_name === filename) ||
                      (m.fileName === row.file_name || m.file_name === row.file_name))) {
                    return true;
                  }
                  
                  return false;
                });

                if (isDuplicate) {
                  return prev;
                }

                const incoming = {
                  from: row.sender,
                  body: row.message,
                  dateSent: row.date_time,
                  type: row.sender === "user" ? "received" : "sent",
                  sid: row.message_sid || null,
                  isReply: row.is_reply || false,
                  originalMessageSid: row.original_message_sid || null,
                  replyToBody: row.reply_to_body || null,
                  replyToMediaUrl: row.reply_to_media_url || null,
                  replyToMediaType: row.reply_to_media_type || null,
                  replyToFileName: row.reply_to_file_name || null,
                  replyToFileSize: row.reply_to_file_size || null,
                  sender: row.sender,
                  message: row.message,
                  date_time: row.date_time,
                  direct_response: row.direct_response || false,
                  context: row.context,
                  user_id: row.user_id,
                  assistant_id: row.assistant_id,
                  media_url: row.media_url,
                  media_type: row.media_type,
                  file_name: row.file_name,
                  file_size: row.file_size,
                  imageUrl: row.media_url,
                  fileName: row.file_name,
                  fileSize: row.file_size,
                  messageType: "document",
                };

                return [...prev, incoming];
              });
            }
          } else if (isImageMessage) {
            if (!row.media_url || !row.media_type || !row.file_name) {
              return;
            }

            const filenameMatch = row.message?.match(/\[Imagen recibida: (.+?)\]/) || row.message?.match(/\[Image received: (.+?)\]/);
            const filename = filenameMatch ? filenameMatch[1] : null;
            
            if (filename) {
              const currentTime = new Date(row.date_time).getTime();
              const timeWindow = 5000; 
              
              setMessages((prev: any) => {
                const isDuplicate = prev.some((m: any) => {

                  if (m.media_url === row.media_url || m.imageUrl === row.media_url) {
                    return true;
                  }
                  
                  const msgTime = new Date(m.date_time || m.dateSent).getTime();
                  const timeDiff = Math.abs(currentTime - msgTime);
                  
                  if (timeDiff < timeWindow && 
                     ((m.fileName === filename || m.file_name === filename) ||
                      (m.fileName === row.file_name || m.file_name === row.file_name))) {
                    return true;
                  }
                  
                  return false;
                });

                if (isDuplicate) {
                  return prev;
                }

                const incoming = {
                  from: row.sender,
                  body: row.message,
                  dateSent: row.date_time,
                  type: row.sender === "user" ? "received" : "sent",
                  sid: row.message_sid || null,
                  isReply: row.is_reply || false,
                  originalMessageSid: row.original_message_sid || null,
                  replyToBody: row.reply_to_body || null,
                  replyToMediaUrl: row.reply_to_media_url || null,
                  replyToMediaType: row.reply_to_media_type || null,
                  replyToFileName: row.reply_to_file_name || null,
                  replyToFileSize: row.reply_to_file_size || null,
                  sender: row.sender,
                  message: row.message,
                  date_time: row.date_time,
                  direct_response: row.direct_response || false,
                  context: row.context,
                  user_id: row.user_id,
                  assistant_id: row.assistant_id,
                  media_url: row.media_url,
                  media_type: row.media_type,
                  file_name: row.file_name,
                  file_size: row.file_size,
                  imageUrl: row.media_url,
                  fileName: row.file_name,
                  fileSize: row.file_size,
                  messageType: "image",
                };

                return [...prev, incoming];
              });
            }
          } else if (isAudioMessage) {
            if (!row.media_url || !row.media_type || !row.file_name) {
              return;
            }

            const filenameMatch = row.message?.match(/\[Audio recibido: (.+?)\]/) || row.message?.match(/\[Audio received: (.+?)\]/);
            const filename = filenameMatch ? filenameMatch[1] : null;
            
            if (filename) {
              const currentTime = new Date(row.date_time).getTime();
              const timeWindow = 5000;
              
              setMessages((prev: any) => {
                const isDuplicate = prev.some((m: any) => {
                  if (m.media_url === row.media_url || m.imageUrl === row.media_url) {
                    return true;
                  }
                  
                  const msgTime = new Date(m.date_time || m.dateSent).getTime();
                  const timeDiff = Math.abs(currentTime - msgTime);
                  
                  if (timeDiff < timeWindow && 
                     ((m.fileName === filename || m.file_name === filename) ||
                      (m.fileName === row.file_name || m.file_name === row.file_name))) {
                    return true;
                  }
                  
                  return false;
                });

                if (isDuplicate) {
                  return prev;
                }

                const incoming = {
                  from: row.sender,
                  body: row.message,
                  dateSent: row.date_time,
                  type: row.sender === "user" ? "received" : "sent",
                  sid: row.message_sid || null,
                  isReply: row.is_reply || false,
                  originalMessageSid: row.original_message_sid || null,
                  replyToBody: row.reply_to_body || null,
                  replyToMediaUrl: row.reply_to_media_url || null,
                  replyToMediaType: row.reply_to_media_type || null,
                  replyToFileName: row.reply_to_file_name || null,
                  replyToFileSize: row.reply_to_file_size || null,
                  sender: row.sender,
                  message: row.message,
                  date_time: row.date_time,
                  direct_response: row.direct_response || false,
                  context: row.context,
                  user_id: row.user_id,
                  assistant_id: row.assistant_id,
                  media_url: row.media_url,
                  media_type: row.media_type,
                  file_name: row.file_name,
                  file_size: row.file_size,
                  imageUrl: row.media_url,
                  fileName: row.file_name,
                  fileSize: row.file_size,
                  messageType: "audio",
                };

                return [...prev, incoming];
              });
            }
          } else {
            const incoming = {
              from: row.sender,
              body: row.message,
              dateSent: row.date_time,
              type: row.sender === "user" ? "received" : "sent",
              sid: row.message_sid || null,
              isReply: row.is_reply || false,
              originalMessageSid: row.original_message_sid || null,
              replyToBody: row.reply_to_body || null,
              replyToMediaUrl: row.reply_to_media_url || null,
              replyToMediaType: row.reply_to_media_type || null,
              replyToFileName: row.reply_to_file_name || null,
              replyToFileSize: row.reply_to_file_size || null,
              sender: row.sender,
              message: row.message,
              date_time: row.date_time,
              direct_response: row.direct_response || false,
              context: row.context,
              user_id: row.user_id,
              assistant_id: row.assistant_id,
              media_url: row.media_url,
              media_type: row.media_type,
              file_name: row.file_name,
              file_size: row.file_size,
              imageUrl: row.media_url,
              fileName: row.file_name,
              fileSize: row.file_size,
              messageType: row.media_type === "image" ? "image" : "text",
            };

            setMessages((prev: any) => {
              const isDuplicate = prev.some((m: any) => {
                if (m.body !== incoming.body) return false;
                
                const msgTime = new Date(m.date_time || m.dateSent).getTime();
                const incomingTime = new Date(incoming.date_time || incoming.dateSent).getTime();
                const timeDiff = Math.abs(msgTime - incomingTime);
                
                if (timeDiff < 5000) {
                  return true;
                }
                
                return false;
              });
              
              if (isDuplicate) {
                return prev;
              }
              
              return [...prev, incoming];
            });
          }

          setDataSent(row.date_time || null);
          if (activePhone) invalidateHistory(activePhone);

        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session, activePhone, selectedUserData?.id, isHistoryPage]);

  useEffect(() => {
    if (myUsers.length > 0 && usersWithHistory.size === 0) {
      fetchAllHistories();
    }
  }, [myUsers, usersWithHistory, fetchAllHistories]);

  return {
    messages,
    setMessages,
    userConversations,
    conversationCounts,
    setConversationCounts,
    usersWithHistory,
    setLoadingUsersWithHistory,
    loadingUsersWithHistory,
    selectedUserData,
    setSelectedUserData,
    setActivePhone,
    fetchMessageHistory,
    setSelectedConversationId,
    setSelectedConversationLastDate,
    setUsersWithHistory,
    dataSent,
    setDataSent,
    error,
    setError,
    handleSendMessage,
  };
}

export function useNotificationFlags({
  activeNotification,
  session,
  t,
  userInfo,
  fetchPendingTransfers,
  fetchAllUsersInfo,
  setUserInfo,
  setReturnControlToAi,
  setActivePhone,
  clearTransferNotification,
  setActiveNotification,
  setSelectedConversationId,
  notifications,
  allAssistants,
}: any) {
  const [resolved, setResolved] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [ignored, setIgnored] = useState(false);
  
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (activeNotification) {
      setResolved(activeNotification.read);
      setUrgent(activeNotification.urgent);
      setIgnored(activeNotification.ignored);
    } else {
      setResolved(false);
      setUrgent(false);
      setIgnored(false);
    }
  }, [activeNotification]);

  const refreshData = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    
    try {
      await Promise.all([
        fetchPendingTransfers(),
      ]);
    } finally {
      isFetchingRef.current = false;
    }
  };

  const markTransferAsResolved = async (closureContext: string) => {
    try {
      if (!activeNotification) return;

      const uidKey = String(activeNotification.userId);
      const list = (notifications?.transfers?.[uidKey] ?? []) as Array<{
        transferId: number;
        is_read?: boolean;
        is_ignored?: boolean;
      }>;

      const ids = Array.from(
        new Set<number>(
          [
            ...list
              .filter((tr) => !tr.is_read && !tr.is_ignored)
              .map((tr) => Number(tr.transferId)),
            Number(activeNotification.transferId),
          ].filter(Boolean)
        )
      );

      const headers = {
        Authorization: `${session!.token_type} ${session!.access_token}`,
        "Content-Type": "application/json",
      };

      const markReqs = ids.map((id) =>
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/communications/markTransferAsResolved/${id}/${activeNotification.userId}`,
          {
            method: "PATCH",
            headers,
            body: JSON.stringify({
              is_read: true,
              closure_context: closureContext,
            }),
          }
        )
      );

      const resolvedByReqs = ids.map((id) =>
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/resolvedNotification`,
          {
            method: "PATCH",
            headers,
            body: JSON.stringify({
              assistantId: session?.user.id,
              notificationId: id,
            }),
          }
        )
      );

      const unassignReqs = ids.map((id) =>
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/assignedToAgent`,
          {
            method: "PATCH",
            headers,
            body: JSON.stringify({
              notificationId: id,
              value: null,
            }),
          }
        )
      );

      const results = await Promise.allSettled([
        ...markReqs,
        ...resolvedByReqs,
        ...unassignReqs,
      ]);

      const allFailed = results.every(
        (r) =>
          r.status === "rejected" ||
          (r.status === "fulfilled" && !(r.value as Response).ok)
      );
      if (allFailed)
        throw new Error("Failed to mark notifications as resolved");

      ids.forEach((id) => clearTransferNotification?.(uidKey, id));

      await refreshData();
      setActiveNotification(null);
      setActivePhone(null);
      setSelectedConversationId(null);
      setReturnControlToAi(true);
      toast.success(t("task_resolved"));
    } catch (error) {
      console.error("Error marking transfer as resolved:", error);
    }
  };

  const markTransferAsUrgent = async (value: boolean) => {
    try {
      if (!activeNotification) return;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/communications/markTransferAsUrgent/${activeNotification.transferId}/${activeNotification.userId}/${value}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `${session!.token_type} ${session!.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ is_urgent: value }),
        }
      );
      if (!response.ok) throw new Error("Failed to mark transfer as important");
      setReturnControlToAi(true);
    } catch (err) {
      console.error("Error marking transfer as urgent:", err);
    }
  };

  const markTransferAsIgnored = async (company_id: any) => {
    try {
      if (!activeNotification) return;
      const companyIdVal =
        company_id && typeof company_id === "object"
          ? company_id.id
          : company_id;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/communications/markTransferAsIgnored/${activeNotification.transferId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `${session!.token_type} ${session!.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const eligibleAssistants =
        (allAssistants || []).filter(
          (a: any) =>
            Array.isArray(a.companies) &&
            a.companies.some((c: any) => String(c.id) === String(companyIdVal))
        ) || [];

      const eligibleIds = eligibleAssistants.map((a: any) => a.id);

      const { data: openNotifs, error: nErr } = await supabase
        .from("notifications")
        .select("assigned_to,is_read,is_ignored")
        .in("assigned_to", eligibleIds);

      if (nErr) throw nErr;

      const load = new Map<string, number>();
      eligibleIds.forEach((id: string) => load.set(id, 0));

      (openNotifs || []).forEach((n: any) => {
        const aid = n.assigned_to as string | null;
        const isRead = Boolean(n.is_read);
        const isIgnored = Boolean(n.is_ignored);
        if (aid && !isRead && !isIgnored && load.has(aid)) {
          load.set(aid, (load.get(aid) || 0) + 1);
        }
      });

      let chosenId: string | null = null;
      let min = Number.POSITIVE_INFINITY;
      for (const id of eligibleIds) {
        const w = load.get(id) ?? 0;
        if (w < min || (w === min && (chosenId == null || id < chosenId))) {
          min = w;
          chosenId = id;
        }
      }

      if (!chosenId) {
        await afterUpdateUI();
        return;
      }

      const responseIgnore = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/assignedToAgent`,
        {
          method: "PATCH",
          headers: {
            Authorization: `${session!.token_type} ${session!.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            notificationId: activeNotification.transferId,
            value: chosenId,
            note: null,
          }),
        }
      );

      if (!response.ok && !responseIgnore.ok) {
        throw new Error("Failed to mark transfer as ignored");
      }

      await afterUpdateUI();
      toast.success(t("ticket_ignored"));
    } catch (err) {
      console.error("Error marking transfer as ignored:", err);
    }
  };

  const afterUpdateUI = async () => {
    clearTransferNotification?.(
      String(activeNotification.userId),
      Number(activeNotification.transferId)
    );
    await refreshData();
    setActiveNotification(null);
    setActivePhone(null);
    setSelectedConversationId(null);
    setReturnControlToAi(true);
  };

  const handleResolvedChange = async (
    checked: boolean,
    closureContext: string
  ) => {
    setResolved(checked);
    if (checked) {
      await markTransferAsResolved(closureContext);
    }
  };

  const handleUrgentChange = async (checked: boolean) => {
    setUrgent(checked);
    if (checked) {
      await markTransferAsUrgent(checked);
    }
  };

  const handleIgnoredChange = async (checked: boolean, company: any) => {
    setIgnored(checked);
    if (checked) {
      await markTransferAsIgnored(company);
      setActivePhone(null);
    }
  };

  return {
    resolved,
    urgent,
    ignored,
    handleResolvedChange,
    handleUrgentChange,
    handleIgnoredChange,
  };
}

export function useAiBlock({
  selectedUserData,
  session,
  refreshTokenIfNeeded,
  setError,
}: any) {
  const [aiBlock, setAiBlock] = useState(false);
  const [aiUpdating, setAiUpdating] = useState(false);

  useEffect(() => {
    if (selectedUserData) setAiBlock(selectedUserData.ai_block);
  }, [selectedUserData]);

  const toggleAiBlock = async () => {
    if (!selectedUserData || aiUpdating) return;

    try {
      setAiUpdating(true);
      await refreshTokenIfNeeded();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/communications/toggleAiBlock/${selectedUserData.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `${session!.token_type} ${session!.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ blocked_by: session!.user.id }),
        }
      );

      if (response.ok) {
        setAiBlock((prev: boolean) => !prev);
      }
    } catch (err: any) {
      console.error("Error toggling ai_block:", err);
      setError({ message: err.message, code: "OMN-010" });
    } finally {
      setAiUpdating(false);
    }
  };

  return { aiBlock, toggleAiBlock, aiUpdating };
}

export function useReassign({
  activeNotification,
  session,
  allAssistants,
  userInfo,
  selectedAssistantId,
  reassignNote,
  fetchPendingTransfers,
  fetchAllUsersInfo,
  setActiveNotification,
  setActivePhone,
  setSelectedConversationId,
}: {
  activeNotification: any;
  session: any;
  allAssistants: any[];
  userInfo: any;
  selectedAssistantId: string | null;
  reassignNote: string;
  fetchPendingTransfers: any;
  fetchAllUsersInfo: any;
  setActiveNotification: any;
  setActivePhone: any;
  setSelectedConversationId: any;
}) {
  const isFetchingRef = useRef(false);

  const reassignNotification = useCallback(
    async (targetId: any) => {
      try {
        const targetAgent = targetId.id;
        const notifId = activeNotification.transferId!;
        const currentId = session!.user.id;

        try {
          const targetAssistant = allAssistants.find(
            (a: any) => a.id === targetId.id
          );
          if (!targetAssistant) {
            throw new Error("No se encontró el asistente de destino.");
          }

          await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/addCollaboration/${notifId}`,
            {
              method: "PATCH",
              headers: {
                Authorization: `${session!.token_type} ${session!.access_token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                collaboration: {
                  reassign_by: userInfo.name,
                  reassign_note: reassignNote || null,
                  reasign_date: new Date().toISOString(),
                },
                assigned_by: currentId,
              }),
            }
          );

          await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/assignedToAgent`,
            {
              method: "PATCH",
              headers: {
                Authorization: `${session!.token_type} ${session!.access_token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                notificationId: notifId,
                value: targetAgent,
              }),
            }
          );

          toast.success("Reasignación exitosa");

          if (isFetchingRef.current) return;
          isFetchingRef.current = true;

          try {
            await fetchPendingTransfers();
          } finally {
            isFetchingRef.current = false;
          }

          setActiveNotification(null);
          setActivePhone(null);
          setSelectedConversationId(null);
        } catch (err) {
          console.error("Error reasignando notificación:", err);
          alert("No se pudo reasignar la notificación.");
        }
      } catch (err) {
        console.error("Error reasignando notificación:", err);
        alert("No se pudo reasignar la notificación.");
      }
    },
    [
      activeNotification,
      session,
      allAssistants,
      userInfo,
      selectedAssistantId,
      reassignNote,
    ]
  );

  return { reassignNotification };
}

export type Message = {
  // Original fields
  from: string;
  body: string;
  type: "sent" | "received";
  sid?: string;
  dateSent?: string;
  isReply?: boolean;
  originalMessageSid?: string;
  replyToBody?: string;
  
  // Reply media fields
  replyToMediaUrl?: string;
  replyToMediaType?: string;
  replyToFileName?: string;
  replyToFileSize?: number;
  
  // All communication_log table fields
  id?: number;
  sender: string;
  message: string;
  date_time: string;
  direct_response: boolean;
  context?: string;
  user_id: string;
  assistant_id?: string;
  media_url?: string;
  media_type?: string;
  file_name?: string;
  file_size?: number;
  
  // Computed/convenience fields
  imageUrl?: string;
  fileName?: string;
  fileSize?: number;
  messageType?: "text" | "image" | "audio" | "assistant" | "document";
  messageSid?: string;
  tempId?: string; // Identificador temporal
};

export function useCompanyFaqs({ session, userInfo, enabled = true }: { session: any, userInfo: any, enabled?: boolean }) {
  const [faqs, setFaqs] = useState<Array<{
    id: number;
    company_id: number;
    section_content: string;
    created_at: string;
    center_id: number | null;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const companyId = userInfo?.companies?.[0]?.id;

  const fetchFaqs = useCallback(async () => {
    if (!session || !companyId || !enabled) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/communications/faqs/${companyId}`,
        {
          method: "GET",
          headers: {
            Authorization: `${session.token_type} ${session.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch FAQs");
      }

      const data = await response.json();
      setFaqs(data);
    } catch (err: any) {
      console.error("Error fetching FAQs:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [session, companyId, enabled]);

  useEffect(() => {
    if (companyId && enabled) {
      fetchFaqs();
    }
  }, [fetchFaqs, companyId, enabled]);

  return {
    faqs,
    loading,
    error,
    refetchFaqs: fetchFaqs,
  };
}
